import { useForm, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import Button from '../../../atoms/Button';
import FormField from '../../../FormField/FormField';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSharedValidations } from '../../../../hooks/useSharedValidations';
import {
  useAddLanguageMutation,
  useGetLanguagesLkQuery,
  useGetProficienciesLkQuery,
  useUpdateLanguageMutation,
} from '../../../../store/services/sellerProfileCreationService';

export default function LanguageForm({
  initData,
  onSuccess,
}: {
  initData?: { id: string | number; language: string; proficiency: string };
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { zodRequiredStringValidate } = useSharedValidations();

  const schema = z.object({
    language: zodRequiredStringValidate,
    proficiency: zodRequiredStringValidate,
  });

  const method = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      language: initData?.language || undefined,
      proficiency: initData?.proficiency || undefined,
    },
  });

  const { sellerId } = useParams<{ sellerId: string }>();
  const profileId = sellerId || '';

  const [addLanguage, { isLoading: isAddLoading }] = useAddLanguageMutation();
  const [updateLanguage, { isLoading: isUpdateLoading }] = useUpdateLanguageMutation();
  const { data: languagesLk } = useGetLanguagesLkQuery();
  const { data: proficienciesLk } = useGetProficienciesLkQuery();

  const submitForm = async (dataForm: any) => {
    try {
      if (initData?.id) {
        await updateLanguage({
          profileId,
          id: String(initData.id),
          languageId: dataForm.language,
          proficiencyId: dataForm.proficiency,
        }).unwrap();
      } else {
        await addLanguage({
          profileId,
          languageId: dataForm.language,
          proficiencyId: dataForm.proficiency,
        }).unwrap();
      }
      onSuccess();
    } catch (err: any) {
      console.error(err);
      const errMsg = err?.data?.errors?.default || err?.data?.message || t('components.somethingWentWrong');
      message.error(errMsg);
    }
  };

  return (
    <FormProvider {...method}>
      <form onSubmit={method.handleSubmit(submitForm)} className="flex flex-col gap-8 pb-3" data-testid="language-form">
        <div className="flex gap-6">
          <div className="flex-1">
            <FormField
              id="language"
              name="language"
              type="select"
              label={t('components.language')}
              placeholder={t('components.selectLanguage')}
              options={languagesLk?.payload?.map((item: any) => ({
                label: item.name,
                value: item.id?.toString(),
              })) || []}
              data-testid="language-form-language"
            />
          </div>
          <div className="flex-1">
            <FormField
              id="proficiency"
              name="proficiency"
              type="select"
              label={t('components.proficiency')}
              placeholder={t('components.selectProficiency')}
              options={proficienciesLk?.payload?.map((item: any) => ({
                label: item.name,
                value: item.id?.toString(),
              })) || []}
              data-testid="language-form-proficiency"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <Button type="submit" disabled={isAddLoading || isUpdateLoading} data-testid="language-form-submit-button">
            {initData?.id ? t('components.save') : t('components.add')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
